import { useEffect, useMemo, useState } from 'react';
import { useLeads, useStages } from '../api/queries';
import { useAuth } from '../auth/AuthProvider';
import { AuditView } from '../components/AuditView';
import { CommandPalette } from '../components/CommandPalette';
import { KanbanView } from '../components/KanbanView';
import { LeadDetail } from '../components/LeadDetail';
import { ListView } from '../components/ListView';
import { OnboardingWizard } from '../components/OnboardingWizard';
import { AddLeadModal } from '../components/modals/AddLeadModal';
import { CreateOrgModal } from '../components/modals/CreateOrgModal';
import { ImportModal } from '../components/modals/ImportModal';
import { OverviewView } from '../components/OverviewView';
import { ReportsView } from '../components/ReportsView';
import { SettingsView } from '../components/SettingsView';
import { MobileNav, TopBar, type ViewKey } from '../components/TopBar';
import { isOverdue } from '../utils/format';

export function MainApp() {
  const [view, setView] = useState<ViewKey>('overview');
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);

  const { me } = useAuth();
  const stagesQ = useStages();
  const leadsQ = useLeads();

  const stages = stagesQ.data?.items ?? [];
  const leads = leadsQ.data?.items ?? [];

  const overdueCount = useMemo(() => {
    const wonId = stages.find((s) => s.isWon)?.id;
    const lostId = stages.find((s) => s.isLost)?.id;
    return leads.filter(
      (l) => isOverdue(l.nextActionDate) && l.stageId !== wonId && l.stageId !== lostId,
    ).length;
  }, [leads, stages]);

  // Cmd+K / Ctrl+K opens the palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowPalette((v) => !v);
      }
      if (e.key === '/' && !showPalette) {
        const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
        if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') {
          e.preventDefault();
          setShowPalette(true);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPalette]);

  // Show onboarding wizard exactly once per user (server-side flag).
  const showOnboarding = !!me && !me.onboardedAt;

  return (
    <div className="flex flex-col h-app">
      <TopBar
        view={view}
        setView={setView}
        onNewLead={() => setShowAddLead(true)}
        onImport={() => setShowImport(true)}
        onOpenPalette={() => setShowPalette(true)}
        onCreateOrg={() => setShowCreateOrg(true)}
        overdueCount={overdueCount}
      />
      <div className="flex-1 min-h-0 overflow-hidden">
        {view === 'overview' && <OverviewView onSelectLead={setSelectedLeadId} />}
        {view === 'kanban' && <KanbanView onSelectLead={setSelectedLeadId} />}
        {view === 'list' && <ListView onSelectLead={setSelectedLeadId} />}
        {view === 'reports' && <ReportsView />}
        {view === 'audit' && <AuditView />}
        {view === 'settings' && <SettingsView />}
      </div>
      <MobileNav
        view={view}
        setView={setView}
        onNewLead={() => setShowAddLead(true)}
        onOpenPalette={() => setShowPalette(true)}
        onImport={() => setShowImport(true)}
      />

      {selectedLeadId && (
        <LeadDetail leadId={selectedLeadId} onClose={() => setSelectedLeadId(null)} />
      )}

      {showAddLead && (
        <AddLeadModal
          onClose={() => setShowAddLead(false)}
          onCreated={(lead, open) => {
            if (open) setSelectedLeadId(lead.id);
          }}
        />
      )}
      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
      {showCreateOrg && <CreateOrgModal onClose={() => setShowCreateOrg(false)} />}
      <CommandPalette
        open={showPalette}
        onClose={() => setShowPalette(false)}
        onPickLead={(id) => setSelectedLeadId(id)}
        onSwitchView={setView}
        onNewLead={() => setShowAddLead(true)}
      />
      {showOnboarding && <OnboardingWizard onClose={() => undefined} />}
    </div>
  );
}
